import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'
import { useFmtPrice } from '../org/OrgContext.jsx'

// Admin: the monthly prepay offers.
//
// The job runs unattended on the 1st (cron 0 7 1 * *), right AFTER monthly
// billing at 0 6 1 * *. This page opens on a DRY RUN — who would be mailed
// right now and for how much — and the button sends for real.
//
// It also reports how the month CONVERTED. There is no "accepted" flag
// anywhere: acceptance is the existence of a paid prepay payment, so the
// numbers below are derived, never stored.
//
// Re-running is safe: a (participant, course, month) already in the send log
// is skipped, so only lines that appeared since the last run get mailed.

export function AdminPrepayPage() {
  const { t } = useTranslation()
  const { user: me, loading: authLoading } = useAuth()
  const fmtPrice = useFmtPrice()

  const [preview, setPreview] = useState(null)
  const [done, setDone] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  function reload() {
    setLoading(true); setError(null)
    api.admin.prepay.preview()
      .then((d) => setPreview(d))
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin') return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me])

  async function run() {
    if (!confirm(t('admin_prepay.confirm_run', { count: preview?.users ?? 0 }))) return
    setBusy(true); setError(null); setDone(null)
    try {
      const d = await api.admin.prepay.run()
      setDone(d)
      reload()
    } catch (e) {
      setError(e.code || 'server')
    } finally { setBusy(false) }
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  const stats   = preview?.stats || {}
  const skipped = preview?.skipped || null
  const nothing = !loading && !skipped && (preview?.lines ?? 0) === 0

  return (
    <main className="container">
      <p><Link to="/admin">&larr; {t('admin.title')}</Link></p>
      <h1>{t('admin_prepay.title')}</h1>
      <p className="muted">{t('admin_prepay.intro')}</p>
      <p className="muted small">{t('admin_prepay.note_derived')}</p>

      {error && (
        <p className="error">
          {t(`admin_prepay.errors.${error}`, {
            defaultValue: t(`admin.errors.${error}`, { defaultValue: error }),
          })}
        </p>
      )}

      {done && (
        <p className="success">
          {t('admin_prepay.done', {
            users: t('admin_prepay.n_users', { count: done.notified ?? 0 }),
            lines: t('admin_prepay.n_lines', { count: done.lines ?? 0 }),
            amount: fmtPrice(done.amount ?? 0),
          })}
        </p>
      )}

      {loading && <p>{t('common.loading')}</p>}

      {!loading && preview && (
        <>
          <section className="card">
            <h2>{t('admin_prepay.preview_title', { month: preview.month })}</h2>

            {skipped && <p className="muted">{t(`admin_prepay.skipped_${skipped}`, { defaultValue: skipped })}</p>}

            {!skipped && (
              <p className="muted small">
                {t('admin_prepay.settings_line', { percent: preview.settings?.percent ?? 0 })}
              </p>
            )}

            {nothing
              ? <p className="muted">{t('admin_prepay.nothing')}</p>
              : !skipped && (
                <p>
                  {t('admin_prepay.summary', {
                    users: t('admin_prepay.n_users', { count: preview.users ?? 0 }),
                    lines: t('admin_prepay.n_lines', { count: preview.lines ?? 0 }),
                  })}
                  {' '}
                  <strong>{fmtPrice(preview.amount ?? 0)}</strong>
                  {' '}
                  <span className="muted">
                    ({t('admin_prepay.saving_total', { amount: fmtPrice(preview.saving ?? 0) })})
                  </span>
                </p>
              )}

            <div className="form-actions">
              <button type="button" disabled={busy || nothing || !!skipped} onClick={run}>
                {busy ? t('common.loading') : t('admin_prepay.run')}
              </button>
              <button type="button" className="link-button" disabled={busy} onClick={reload}
                      style={{ marginLeft: '0.75rem' }}>
                {t('admin_prepay.refresh')}
              </button>
            </div>
          </section>

          <section className="card">
            <h2>{t('admin_prepay.stats_title')}</h2>
            <p className="muted small">{t('admin_prepay.stats_hint')}</p>
            <table className="results-table">
              <tbody>
                <tr>
                  <th>{t('admin_prepay.stat_offered')}</th>
                  <td>{stats.offered ?? 0}</td>
                  <td>{fmtPrice(stats.offered_amount ?? 0)}</td>
                </tr>
                <tr>
                  <th>{t('admin_prepay.stat_accepted')}</th>
                  <td>{stats.accepted ?? 0}</td>
                  <td>{fmtPrice(stats.accepted_amount ?? 0)}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </>
      )}
    </main>
  )
}
